import { useCallback, useMemo, useState } from 'react';
import type { IteroOrder } from '../../../../services/iteroBrowse';
import { NO_SELECTION } from './iteroBrowserRoute';
import type { OrderTabId } from './order-view/orderTabIds';
import type { IteroTabNav } from './useIteroBrowserNavigation';
import {
    ORDER_UNAVAILABLE_TEXT,
    useDropUnavailable,
    useRestoredOrder,
    type Restored
} from './useRestoredSelection';

export interface OrderSelection {
    selectedRxId: string | null;
    orderTab: OrderTabId;
    order: Restored<IteroOrder>;
    select: (order: IteroOrder) => void;
    close: () => void;
    setOrderTab: (tab: OrderTabId) => void;
}

function findOrder(
    rxId: string | null,
    known: IteroOrder | null,
    rows: readonly IteroOrder[]
): IteroOrder | null {
    if (rxId === null) return null;
    if (known?.rxId === rxId) return known;
    return rows.find((row) => row.rxId === rxId) ?? null;
}

// The URL holds which order is open; the record comes from the click, the list or a fetch by rxId.
export function useOrderSelection(nav: IteroTabNav, rows: readonly IteroOrder[]): OrderSelection {
    const orderRef = nav.selection.order;
    const rxId = orderRef?.rxId ?? null;
    const [known, setKnown] = useState<IteroOrder | null>(null);
    const order = useRestoredOrder(rxId, findOrder(rxId, known, rows));

    const drop = useCallback(() => nav.replace(NO_SELECTION), [nav]);
    useDropUnavailable(order, rxId, ORDER_UNAVAILABLE_TEXT, drop);

    const select = useCallback(
        (next: IteroOrder) => {
            if (next.rxId === rxId) return;
            setKnown(next);
            nav.push({ patient: null, order: { rxId: next.rxId, tab: 'details' } });
        },
        [nav, rxId]
    );

    const setOrderTab = useCallback(
        (tab: OrderTabId) => {
            if (orderRef) nav.replace({ patient: null, order: { ...orderRef, tab } });
        },
        [nav, orderRef]
    );

    return useMemo(
        () => ({
            selectedRxId: rxId,
            orderTab: orderRef?.tab ?? 'details',
            order,
            select,
            close: nav.stepBack,
            setOrderTab
        }),
        [rxId, orderRef, order, select, nav.stepBack, setOrderTab]
    );
}
